import { useState } from "react"
import { observer } from "mobx-react-lite"
import { useAccount, useConfig } from "wagmi"

import { Button } from "@/components/button"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { ArrowDownCappedIcon, MultiNftIcon } from "@/domains/icons"
import { CoinsIcon } from "@/domains/icons/huge-icons/monotone/coins"
import { FeesIcon } from "@/domains/icons/huge-icons/monotone/fees"
import { claimLpFeesInteraction } from "@/domains/interactions"
import { cn, dollars, dollarsPrettified } from "@/domains/library"
import { DisplayTokens } from "@/domains/tokens/components/display-tokens"
import { useChainConfig } from "@/hooks/use-chain-config"
import useEventEmitter from "@/hooks/useEventEmitter"

export const EarnedFeesSummaryCard = observer(() => {
  const account = useAccount()
  const chainConfig = useChainConfig()
  const config = useConfig()
  const emitter = useEventEmitter()
  const scaleApp = useScaleApplication()
  const [isClaiming, setIsClaiming] = useState(false)

  // Get all pools
  const allPools = scaleApp.MinimalPools.Repository.getAll()

  // Filter for pools with positions or claimable fees
  const feePools = allPools.filter(
    (pool) =>
      pool.onChainUserBalance > 0n || pool.onChainUserClaimableFees0 > 0n || pool.onChainUserClaimableFees1 > 0n,
  )

  // Calculate total deposited (only unstaked positions, not staked)
  // For earned fees, we only count unstaked positions since staked positions earn farming rewards, not fees
  const poolsWithUnstakedDeposits = feePools.filter((pool) => pool.onChainUserBalance > 0n)
  const totalDeposited = poolsWithUnstakedDeposits.reduce((sum, pool) => sum + pool.userBalanceValueUSD, 0)

  // Calculate total earned fees USD
  const earnedFeesTotalUSD = feePools.reduce((sum, pool) => {
    const fees0Amount = Number(pool.token0.formatUnits(pool.onChainUserClaimableFees0))
    const fees1Amount = Number(pool.token1.formatUnits(pool.onChainUserClaimableFees1))
    const fees0USD = pool.token0.calculateValueUSD(fees0Amount)
    const fees1USD = pool.token1.calculateValueUSD(fees1Amount)
    return sum + fees0USD + fees1USD
  }, 0)

  // Get unique earned tokens (from pools with claimable fees > 0)
  const earnedTokensMap = new Map<string, (typeof feePools)[0]["token0"]>()
  feePools.forEach((pool) => {
    if (pool.onChainUserClaimableFees0 > 0n) {
      const key = pool.token0.address.toString()
      if (!earnedTokensMap.has(key)) {
        earnedTokensMap.set(key, pool.token0)
      }
    }
    if (pool.onChainUserClaimableFees1 > 0n) {
      const key = pool.token1.address.toString()
      if (!earnedTokensMap.has(key)) {
        earnedTokensMap.set(key, pool.token1)
      }
    }
  })
  const uniqueEarnedTokens = Array.from(earnedTokensMap.values())

  // Position count
  const positionCount = feePools.length

  // Check if there are fees to claim
  const hasFees = earnedFeesTotalUSD > 0

  const handleClaimAll = async (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent card click from scrolling
    if (!hasFees || isClaiming) return

    setIsClaiming(true)
    try {
      if (!account.address) {
        throw new Error("no address found")
      }

      // Claim fees for all pools with claimable fees
      for (const pool of feePools) {
        if (pool.onChainUserClaimableFees0 === 0n && pool.onChainUserClaimableFees1 === 0n) {
          continue
        }

        await claimLpFeesInteraction({
          account: account.address,
          chainId: chainConfig.chainId,
          config,
          pairAddress: pool.address.toViemAddress() as `0x${string}`,
          pairDisplayName: pool.displayName ?? "unknown",
          emitter,
        })
      }

      // Refresh user info pairs query after claiming
      scaleApp.MinimalPools.Orchestrator.userInfoPairsQueryInstance?.forceExecute()
    } catch (error) {
      console.error("EarnedFeesSummaryCard→handleClaimAll error", error)
    } finally {
      setIsClaiming(false)
    }
  }

  const handleViewDetails = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent card click from scrolling
    const element = document.getElementById("earned-fees")
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  const handleCardClick = () => {
    const element = document.getElementById("earned-fees")
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  return (
    <div
      onClick={handleCardClick}
      className={cn(
        "group relative flex w-full flex-col overflow-hidden rounded-3xl border border-p400-light bg-p300 shadow-2xl transition-all",
        "hover:border-p400-light/80",
      )}
    >
      {/* Header */}
      <div className="relative z-10 p-6 pb-6">
        <div className="mb-6 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-t300/20 bg-p400/10 text-t300">
              <FeesIcon className="h-5 w-5 text-t300" />
            </div>
            <div>
              <h3 className="text-lg font-bold leading-tight text-t100">Earned Fees</h3>
              <p className="text-xs font-medium text-t300">Liquidity Fees</p>
            </div>
          </div>

          {/* Status Pill */}
          {positionCount > 0 && (
            <div className="flex items-center gap-1.5 rounded-full border border-green-500/20 bg-green-500/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-green-400">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-500"></span>
              </span>
              Active
            </div>
          )}
        </div>

        {/* Hero Metrics Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="mb-1 text-[10px] font-bold uppercase text-t300">Total in Wallet</p>
            <div className="text-2xl font-bold tracking-tight text-t100">{dollarsPrettified(totalDeposited, 2)}</div>
          </div>
          <div className="text-right">
            <p className="mb-1 text-[10px] font-bold uppercase text-t300">Unclaimed Rewards</p>
            <div className="flex items-center justify-end gap-2 text-2xl font-bold tracking-tight text-t100">
              <div className="text-2xl font-bold tracking-tight text-t300">
                {dollarsPrettified(earnedFeesTotalUSD, 2)}
              </div>
            </div>
            <div className="mt-0.5 flex items-center justify-end text-[10px] font-medium text-t300">
              <DisplayTokens tokens={uniqueEarnedTokens} tokenSize={14} />
            </div>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 border-y border-t300/[0.12] bg-t300/[0.02]">
        {/* Positions Count */}
        <div className="flex flex-col items-center justify-center border-r border-t300/[0.12] p-4">
          <span className="mb-0.5 text-[10px] font-bold uppercase text-t300">Positions</span>
          <span className="flex items-center gap-1 text-sm font-bold text-t100">
            <MultiNftIcon className="h-3.5 w-3.5 text-t200" /> {positionCount}
          </span>
        </div>

        {/* Top APR - Blank */}
        <div className="flex flex-col items-center justify-center border-r border-t300/[0.12] p-4">
          <span className="mb-0.5 text-[10px] font-bold uppercase text-t300">Top APR</span>
          <span className="text-sm font-bold text-t200">—</span>
        </div>

        {/* Status - Blank */}
        <div className="flex flex-col items-center justify-center p-4">
          <span className="mb-0.5 text-[10px] font-bold uppercase text-t300">Status</span>
          <span className="text-sm font-bold text-t200">—</span>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="z-10 mt-auto flex gap-3 bg-p300 p-4">
        {/* Secondary Action: View Details */}
        <Button
          variant="percentage"
          size="sm"
          onClick={handleViewDetails}
          className="text-xs font-bold uppercase tracking-wide"
        >
          Details <ArrowDownCappedIcon className="h-3.5 w-3.5" />
        </Button>

        {/* Primary Action: Claim All */}
        <Button
          variant={hasFees ? "primary" : "percentage"}
          size="sm"
          onClick={handleClaimAll}
          disabled={!hasFees || isClaiming}
          className="flex-1 font-bold"
        >
          {isClaiming ? (
            <>
              <span className="animate-spin">⟳</span> Claiming...
            </>
          ) : (
            <>
              <CoinsIcon className="h-4 w-4" /> Claim All
            </>
          )}
        </Button>
      </div>
    </div>
  )
})
